import { type DisplayDocument, Document } from '@startuptools/business-ui/document/Document';
import {
  GqlDocSystemTagKind,
  GqlDocumentSignatureMethod,
  type GqlIdentity,
  type GqlMeetingConcern,
  type GqlOptionsSharesSubscriber,
  type GqlValidatorStatus,
  type GqlWarrantProgram,
} from '../../../../graphql/base-types.graphql';
import { Divider } from '@startuptools/ui/Divider';
import {
  useActiveCompanyStripeSubLazyQuery,
  useCompanySuspenseQuery,
  useDocGenOptionsShareSubscriptionListMutation,
  useMeetingPaidForConcernsLazyQuery,
  useOsrErrorsLazyQuery,
  useOssErrorsLazyQuery,
} from '../../../../graphql/react-operations';
import { useCompanyId } from '@startuptools/common/react-wrapper';
import { omit } from 'lodash-es';
import { DocumentSignatures } from '../../../../components/document-signatures/DocumentSignatures';
import { useState } from 'react';
import { apiClient } from '../../../../common/http';
import { checkNeedsPayment, hasValidatorErrors } from '../../utils';
import { ValidatorErrorDialog } from '../ValidatorErrorDialog';
import { InnerStripe } from '../../../../components/stripe/Stripe';
import { StepOsrSubscribersDocument } from '../../../stock-options/common/components/steps/osr-subscribers/options-subscribers.graphql';
import { OptionsSharesSubscriptionNoteListDocument } from './OptionsSharesSubscriptionNoteList.graphql';
import { OptionsDocumentsDocument } from '../../../stock-options/common/components/main-steps/options-documents/OptionsDocuments.graphql';
import { DocGenRelationName } from '@startuptools/common/doc-gen';
import { useApolloClient } from '@apollo/client';
import { useNavigate } from '../../../../components/Navigate';

const REFETCH_QUERIES = [
  StepOsrSubscribersDocument,
  OptionsSharesSubscriptionNoteListDocument,
  OptionsDocumentsDocument,
];

export const OptionsSharesSubscriptionNote = ({
  warrantProgram,
  subscriber,
}: {
  warrantProgram: Pick<GqlWarrantProgram, 'description' | 'companyId' | 'paidForAt'> & {
    meetingConcern?: Pick<GqlMeetingConcern, 'meetingId'> | null;
  };
  subscriber: Pick<GqlOptionsSharesSubscriber, 'id' | 'optionsSharesRegistrationId'> & {
    identity: Pick<GqlIdentity, 'id' | 'name'>;
    latestShareSubscriptionListDoc?: DisplayDocument | null;
  };
}) => {
  const companyId = useCompanyId();
  const gqlClient = useApolloClient();
  const [validatorErrors, setValidatorErrors] = useState<GqlValidatorStatus[] | null>(null);
  const [showStripeDialog, setShowStripeDialog] = useState<{
    open: boolean;
    signatureMethod?: GqlDocumentSignatureMethod;
  }>({ open: false });
  const [fetchPaidFor] = useMeetingPaidForConcernsLazyQuery({ fetchPolicy: 'no-cache' });
  const { navigate } = useNavigate();

  const {
    data: { company },
  } = useCompanySuspenseQuery({ variables: { id: companyId } });
  const [fetchErrors] = useOssErrorsLazyQuery({ variables: { companyId, id: subscriber.id }, fetchPolicy: 'no-cache' });
  const [fetchOsrErrors] = useOsrErrorsLazyQuery({
    variables: { companyId, id: subscriber.optionsSharesRegistrationId },
    fetchPolicy: 'no-cache',
  });

  const [doGetActiveSub] = useActiveCompanyStripeSubLazyQuery({ variables: { companyId }, fetchPolicy: 'no-cache' });

  // eslint-disable-next-line sut/loading-mutation
  const [genDoc] = useDocGenOptionsShareSubscriptionListMutation({
    refetchQueries: REFETCH_QUERIES,
    awaitRefetchQueries: true,
  });

  const defaultFilename = `Teckningsanmälan av aktier p.g.a teckningsoptioner - ${subscriber.identity.name} - ${company.name} - ${warrantProgram.description}.pdf`;

  return (
    <>
      <Document
        signaturesSlot={
          subscriber.latestShareSubscriptionListDoc?.signatureMethod === GqlDocumentSignatureMethod.Digital && (
            <>
              <Divider />
              <DocumentSignatures
                documentId={subscriber.latestShareSubscriptionListDoc.id}
                scriveStatus={subscriber.latestShareSubscriptionListDoc.scriveStatus}
                zignedAgreementStatus={subscriber.latestShareSubscriptionListDoc.zignedAgreementStatus}
                signAllowedAction={subscriber.latestShareSubscriptionListDoc.signAllowedAction}
              />
            </>
          )
        }
        upload={async ({ file, signDigitally }) => {
          await apiClient.uploadFile(
            file,
            defaultFilename,
            company.id,
            {
              relation_id: subscriber.id,
              relation_name: DocGenRelationName.OptionsSharesSubscriberId,
            },
            signDigitally,
            GqlDocSystemTagKind.ShareSubscriptionList,
          );
          await gqlClient.refetchQueries({ include: REFETCH_QUERIES });
        }}
        document={subscriber.latestShareSubscriptionListDoc}
        defaultFileName={defaultFilename}
        generate={async signatureMethod => {
          if (warrantProgram.paidForAt) {
            return genDoc({
              variables: { companyId: warrantProgram.companyId, ossId: subscriber.id, signatureMethod },
            });
          }
          const id = warrantProgram?.meetingConcern?.meetingId;
          if (!id) {
            throw new Error('Unexpected state: no meetingId');
          }
          const { data } = await fetchPaidFor({ variables: { companyId: company.id, id } });
          const paidFor = data?.meetingPaidForConcerns;
          if (!paidFor) {
            throw new Error('Unexpected state: no paidFor');
          }
          const { data: activeSubData } = await doGetActiveSub();
          if (!activeSubData) {
            throw new Error('Could not get active subscription');
          }
          const allMcs = [...paidFor.shareIssues, ...paidFor.warrantPrograms];
          const needsPayment = checkNeedsPayment(activeSubData.activeCompanyStripeSubscription, allMcs);
          if (needsPayment) {
            setShowStripeDialog({ open: true, signatureMethod });
            return;
          }
          return genDoc({
            variables: { companyId: company.id, ossId: subscriber.id, signatureMethod },
          });
        }}
        preview={() => {
          return apiClient.previewOptionsShareSubscriptionList(companyId, subscriber.id);
        }}
        validate={async () => {
          const { data } = await fetchErrors({ fetchPolicy: 'no-cache' });
          const { data: osrErrorData } = await fetchOsrErrors();
          const osrErrors = omit(
            osrErrorData?.optionsSharesRegistrationErrors,
            'participants',
            'signsRegForm',
            'todos',
          );
          const ossErrors = omit(data?.optionsSharesSubscriberErrors, 'paymentStatus');
          if (hasValidatorErrors(osrErrors) || hasValidatorErrors(ossErrors)) {
            const allErrors = [...Object.values(osrErrors), ...Object.values(ossErrors)];
            setValidatorErrors(allErrors);
            return { isValid: false, errors: undefined };
          }
          return { isValid: true, errors: undefined };
        }}
      />
      {validatorErrors && <ValidatorErrorDialog errors={validatorErrors} onClose={() => setValidatorErrors(null)} />}
      {showStripeDialog.open && warrantProgram.meetingConcern?.meetingId && (
        <InnerStripe
          companyId={companyId}
          meetingId={warrantProgram.meetingConcern?.meetingId}
          onCancel={() => setShowStripeDialog({ open: false })}
          onPaid={async () => {
            setShowStripeDialog({ open: false });
            return genDoc({
              variables: {
                companyId: company.id,
                ossId: subscriber.id,
                signatureMethod: showStripeDialog.signatureMethod ?? GqlDocumentSignatureMethod.Analogue,
              },
            });
          }}
          onBillingRedirect={() => navigate('/settings/billing')}
        />
      )}
    </>
  );
};
